import * as A from 'astronomy-engine';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { services, type AppServices } from '../app';
import { moonPhaseName } from '../astro/solar';
import { PLANETS } from '../live/logbook';
import {
  backupText, deleteNight, keepNight, nights, restoreFrom, seenIndex, setNote, sightingName, unsee,
} from '../live/nights';
import { type Night, type Sighting, type SightingKind, nightId, nightLabel, nightMoment, nightSpan } from '../live/nightsCore';
import { endRelive, reliveAt, startRelive } from '../live/relive';
import { bestView, darkOut, openNight, panel, place, previewFrom, reliving, selKey, selection, showToast, simNow, timeLive, timeVersion } from '../state';
import { Sheet } from './chrome';
import { fmtDate, fmtTime } from './format';
import { Icon } from './icons';
import { drawNightChart, type ChartOptions } from './skyDisc';
import { crescentDay, crescentMonthAt } from '../live/saudi';
import { SawCrescent } from './Crescent';

// Your nights: the list, one night's page, and the bar for reliving a night on the sky.

const KIND_ICON: Record<SightingKind, string> = {
  body: 'planet', star: 'star', dso: 'sparkle', constellation: 'star', satellite: 'satellite', aircraft: 'plane', meteor: 'meteor', shower: 'meteor', small: 'meteor', crescent: 'moon',
};
const iconFor = (x: Sighting) => (x.key === 'body:Moon' ? 'moon' : KIND_ICON[x.kind]);

/** A time in the place's own time zone (a night keeps the zone it was seen in). */
const timeIn = (t: number, tz?: string) => new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz }).format(t);

function cloudWords(c: number) {
  return c < 20 ? 'Clear' : c < 50 ? 'Some cloud' : c < 80 ? 'Mostly cloudy' : 'Cloudy';
}
function skyWords(bortle: number) {
  return bortle <= 2 ? 'A truly dark sky' : bortle <= 3 ? 'A rural sky' : bortle <= 4 ? 'A rural–suburban sky' : bortle <= 5 ? 'A suburban sky' : bortle <= 6 ? 'A bright suburban sky' : 'A city sky';
}

/** What else a sighting keeps, in a few words: a satellite's pass, a plane's type. */
function sightingDetail(s: AppServices | null, x: Sighting): string {
  if (x.kind === 'satellite' && x.pass) return ' · its pass is saved';
  if (x.kind === 'aircraft' && x.plane) {
    const name = sightingName(s, x);
    if (x.plane.type) return ` · ${x.plane.type}`;
    if (x.plane.flight && x.plane.flight !== name) return ` · flight ${x.plane.flight}`;
  }
  return '';
}

/** Names in a list: "Saturn, the ISS and 2 more". */
function listNames(s: AppServices | null, n: Night, max = 2) {
  const names = n.sightings.map((x) => sightingName(s, x));
  if (!names.length) return n.kept ? 'The sky, kept' : 'Nothing ticked yet';
  if (names.length <= max + 1) return names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${names.slice(0, max).join(', ')} and ${names.length - max} more`;
}

/** A night's sky, drawn once it scrolls into view (and again if the night changes). */
function SkyDisc({ night, ...o }: { night: Night } & ChartOptions) {
  const ref = useRef<HTMLCanvasElement>(null);
  const s = services.value;
  useEffect(() => {
    const c = ref.current;
    if (!c || !s) return;
    const draw = () => drawNightChart(c, s, night, o);
    if (typeof IntersectionObserver === 'undefined') return draw();
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) {
        io.disconnect();
        draw();
      }
    }, { rootMargin: '200px' });
    io.observe(c);
    return () => io.disconnect();
  }, [night, o.size, !!s]);
  return <canvas ref={ref} class="sky-disc" style={{ width: `${o.size}px`, height: `${o.size}px` }} aria-hidden="true" />;
}

// ---------- The list ----------
export function NightsPanel() {
  const s = services.value!;
  const all = Object.values(nights.value).sort((a, b) => b.id.localeCompare(a.id));
  // Tonight is tonight where the phone is, by the real clock: not a night being relived or the time on show.
  const home = previewFrom.value?.home ?? place.value;
  const tonight = nightId(Date.now(), home.lon);
  const saved = nights.value[tonight];
  // The sky can be kept as it is only when it is the real sky, after dark.
  const canKeep = timeLive.value && !reliving.value && !previewFrom.value && darkOut.value;
  const seen = seenIndex.value;
  const keys = [...seen.keys()];
  const planets = PLANETS.filter((p) => seen.has(`body:${p}`)).length;
  const messier = s.engine.dsos.filter((d) => d.m && seen.has(`dso:${d.id}`)).length;
  const cons = keys.filter((k) => k.startsWith('con:')).length;
  // Passes of the ISS actually seen: ones whose track was saved.
  const iss = all.reduce((k, n) => k + n.sightings.filter((x) => x.key === 'sat:25544' && x.pass).length, 0);
  // From the sunset ending the 29th (or 30th) to the third night: "I saw the crescent", for the real sky here and now.
  const crescent = timeLive.value && !reliving.value && !previewFrom.value ? crescentMonthAt(Date.now(), home) : null;
  const open = (id: string) => {
    openNight.value = id;
    panel.value = 'night';
  };
  const keep = () => {
    const had = !!saved?.kept;
    const id = keepNight(s);
    // Not stored: keepNight has said why.
    if (!id) return;
    showToast(had ? 'Saved the sky as it is now' : 'Tonight is saved');
    open(id);
  };
  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label="Your nights">
      <h2 class="sheet-title">Your nights</h2>
      <p class="sheet-lead">What you have seen, night by night. Kept on this phone only.</p>
      <div class="card wide">
        {seen.size > 0 && (
          <>
            <div class="log-stats">
              <div class="log-stat"><b>{planets}/7</b><span>planets</span></div>
              <div class="log-stat"><b>{cons}/88</b><span>constellations</span></div>
              <div class="log-stat"><b>{messier}/110</b><span>Messier</span></div>
              <div class="log-stat"><b>{iss}</b><span>ISS {iss === 1 ? 'pass' : 'passes'}</span></div>
            </div>
            <p class="fine tally-note">Messier is a classic list of 110 galaxies, star clusters and nebulae to find.</p>
          </>
        )}
        <NewForYou />
        {crescent && <SawCrescent month={crescent} />}
        {saved ? (
          <>
            <button class="btn" onClick={() => open(tonight)}><Icon name="nights" size={18} /> Open tonight</button>
            {canKeep && <button class="btn-ghost keep-now" onClick={keep}>Save the sky as it is now</button>}
          </>
        ) : (
          canKeep && <button class="btn" onClick={keep}><Icon name="nights" size={18} /> Save this night</button>
        )}
        <p class="fine">
          Tick “Seen it” on anything’s card and it’s saved to that night, with where it was in the sky.
          {!canKeep && !saved ? ' After dark you can also save the whole sky as it is.' : ''}
        </p>
      </div>

      <h3 class="section-title">Nights</h3>
      {!all.length && (
        <div class="empty">Your first night starts with “Seen it”: tap anything in the sky, then “Seen it” on its card.</div>
      )}
      <div class="results">
        {all.map((n) => (
          <button class="result night-row" key={n.id} onClick={() => open(n.id)}>
            <SkyDisc night={n} size={48} />
            <span class="result-text">
              <span class="result-label">{n.id === tonight ? 'Tonight' : nightLabel(n.id)}</span>
              <span class="result-sub">{n.place.name} · {listNames(s, n)}</span>
            </span>
            <Icon name="chevron" size={16} />
          </button>
        ))}
      </div>
      <p class="fine">Nothing leaves this phone. To keep your nights safe, or move them to another phone, back them up in Settings › Your nights.</p>
    </Sheet>
  );
}

/** Up tonight and not seen yet (the Moon and the planets): the easiest first, with when to look. */
function NewForYou() {
  const seen = seenIndex.value;
  const home = previewFrom.value?.home ?? place.value;
  const quarter = Math.floor(Date.now() / 900e3);
  const list = useMemo(() => {
    const obs = new A.Observer(home.lat, home.lon, home.height);
    const now = Date.now();
    const out: { id: string; from: number; mag: number }[] = [];
    for (const id of ['Moon', ...PLANETS]) {
      if (seen.has(`body:${id}`)) continue;
      // The two faintest planets need binoculars: offered only once the bright ones have been seen.
      if ((id === 'Uranus' || id === 'Neptune') && PLANETS.slice(0, 5).some((p) => !seen.has(`body:${p}`))) continue;
      const body = A.Body[id as keyof typeof A.Body];
      // The first moment, from now until tomorrow's dawn, that it is dark and it stands 15° up or more.
      for (let t = now; t <= now + 16 * 3600e3; t += 15 * 60e3) {
        const d = new Date(t);
        const se = A.Equator(A.Body.Sun, d, obs, true, true);
        if (A.Horizon(d, obs, se.ra, se.dec, 'normal').altitude > -9) continue;
        const e = A.Equator(body, d, obs, true, true);
        if (A.Horizon(d, obs, e.ra, e.dec, 'normal').altitude < 15) continue;
        out.push({ id, from: t, mag: A.Illumination(body, d).mag });
        break;
      }
    }
    // Evening first (when most people are out), and among things up at about the same time, the brightest.
    return out.sort((a, b) => Math.floor(a.from / 3600e3) - Math.floor(b.from / 3600e3) || a.mag - b.mag).slice(0, 3);
  }, [seen, home.lat, home.lon, quarter]);
  if (!list.length) return null;
  const show = (id: string, t: number) => {
    const sel = { kind: 'body' as const, id };
    if (t > Date.now() + 10 * 60e3) bestView.value = { key: selKey(sel), t };
    panel.value = null;
    selection.value = sel;
  };
  return (
    <div class="new-for-you">
      <span class="new-title"><Icon name="sparkle" size={14} /> New for you tonight</span>
      <div class="new-list">
        {list.map((x) => (
          <button key={x.id} class="btn-ghost small" onClick={() => show(x.id, x.from)}>
            {x.id === 'Moon' ? 'The Moon' : x.id} <span class="new-when">{x.from <= Date.now() + 10 * 60e3 ? 'up now' : `from ${fmtTime(x.from)}`}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- One night ----------
export function NightPage() {
  const s = services.value!;
  const id = openNight.value;
  const n = id ? nights.value[id] : null;
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [note, setNoteText] = useState(n?.note ?? '');
  useEffect(() => setNoteText(n?.note ?? ''), [id]);
  // The note is kept as it is typed (a little after each pause), and whatever is left when the page closes.
  const pending = useRef<{ id: string; note: string } | null>(null);
  useEffect(() => {
    if (!id) return;
    pending.current = { id, note };
    const h = setTimeout(() => {
      setNote(id, note);
      pending.current = null;
    }, 400);
    return () => clearTimeout(h);
  }, [note]);
  useEffect(() => () => {
    if (pending.current) setNote(pending.current.id, pending.current.note);
  }, []);
  useEffect(() => {
    if (!confirmDelete) return;
    const h = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(h);
  }, [confirmDelete]);
  if (!n) {
    return (
      <Sheet onClose={() => (panel.value = 'nights')} size="tall" label="Night">
        <div class="empty">This night is no longer saved.</div>
        <button class="btn-ghost" onClick={() => (panel.value = 'nights')}>Your nights</button>
      </Sheet>
    );
  }
  const tz = n.place.timeZone;
  const [a, b] = nightSpan(n);
  const when = timeIn(a, tz) === timeIn(b, tz) ? timeIn(a, tz) : `${timeIn(a, tz)} to ${timeIn(b, tz)}`;
  const moonPct = Math.round(A.Illumination(A.Body.Moon, new Date(a)).phase_fraction * 100);
  const phase = moonPhaseName(A.MoonPhase(new Date(a)));
  // On the 29th, the 30th and the 1st, just "Crescent" (src/live/saudi.ts).
  const crescent = crescentDay(a, n.place);
  // Closing goes back to the list, or while reliving, back to the sky.
  const back = () => (panel.value = reliving.peek() ? null : 'nights');
  const relive = (x?: Sighting, at?: number) => startRelive(s, n, at, x);
  const undo = (before: typeof nights.value) => ({ label: 'Undo', run: () => (nights.value = before) });
  const del = () => {
    if (!confirmDelete) return setConfirmDelete(true);
    const before = nights.peek();
    if (!deleteNight(n.id)) return;
    showToast(`The night of ${nightLabel(n.id, false)} is deleted`, 6000, undo(before));
    panel.value = 'nights';
  };
  const remove = (x: Sighting) => {
    const before = nights.peek();
    if (!unsee(n.id, x.id)) return;
    const gone = !nights.peek()[n.id];
    showToast(gone ? `${sightingName(s, x)} taken off, and the night with it` : `${sightingName(s, x)} taken off`, 6000, undo(before));
    if (gone) panel.value = 'nights';
  };
  return (
    <Sheet onClose={back} size="tall" labelledBy="night-title">
      <p class="night-kicker">Night of</p>
      <h2 class="sheet-title" id="night-title">{nightLabel(n.id)}</h2>
      <p class="sheet-lead">{n.place.name} · {when}</p>
      <div class="night-disc">
        <SkyDisc night={n} size={Math.min(320, window.innerWidth - 56)} labels lines />
      </div>
      <p class="fine center">The whole sky at {timeIn(nightMoment(n), tz)}: north at the top, east on the left, as when looking up.</p>
      <div class="night-chips">
        <span class="night-chip"><Icon name="moon" size={14} /> {crescent ? 'Crescent' : phase === 'New Moon' || phase === 'Full Moon' ? phase : `Moon ${moonPct}% lit, ${phase.toLowerCase()}`}</span>
        {typeof n.weather?.cloud === 'number' && <span class="night-chip"><Icon name="cloud" size={14} /> {cloudWords(n.weather.cloud)} ({n.weather.cloud}% cloud)</span>}
        {n.sky && <span class="night-chip"><Icon name="eye" size={14} /> {skyWords(n.sky.bortle)}</span>}
        {typeof n.weather?.tempC === 'number' && <span class="night-chip">{n.weather.tempC}°C</span>}
      </div>

      <div class="section-head">
        <h3 class="section-title">What you saw</h3>
        {n.sightings.length > 0 && <button class="btn-ghost small" onClick={() => setEditing(!editing)}>{editing ? 'Done' : 'Edit'}</button>}
      </div>
      <div class="results">
        {n.sightings.map((x) => (
          <div class="result-row" key={x.id}>
            {(() => {
              const inner = (
                <>
                  <span class="result-icon"><Icon name={iconFor(x)} size={20} /></span>
                  <span class="result-text">
                    <span class="result-label">{sightingName(s, x)}</span>
                    <span class="result-sub tnum">{timeIn(x.t, tz)}{sightingDetail(s, x)}</span>
                  </span>
                </>
              );
              return editing ? <div class="result">{inner}</div> : (
                <button class="result" onClick={() => relive(x)} aria-label={`${sightingName(s, x)} at ${timeIn(x.t, tz)}: relive this moment`}>
                  {inner}
                  <Icon name="relive" size={18} />
                </button>
              );
            })()}
            {editing && (
              <button class="remove-btn" aria-label={`Take ${sightingName(s, x)} off this night`} onClick={() => remove(x)}>
                <Icon name="close" size={14} stroke={2.2} />
              </button>
            )}
          </div>
        ))}
        {n.kept && (
          <button class="result" onClick={() => relive(undefined, n.kept!.t)}>
            <span class="result-icon"><Icon name="nights" size={20} /></span>
            <span class="result-text">
              <span class="result-label">The sky as you saved it</span>
              <span class="result-sub tnum">{timeIn(n.kept.t, tz)}</span>
            </span>
            <Icon name="relive" size={18} />
          </button>
        )}
        {!n.sightings.length && !n.kept && <div class="empty">Nothing on this night now.</div>}
      </div>

      <h3 class="section-title" id="night-note-title">Your note</h3>
      <textarea class="night-note" aria-labelledby="night-note-title" rows={3} maxLength={4000} placeholder="Who you were with, what it looked like…" value={note} onInput={(e) => setNoteText((e.target as HTMLTextAreaElement).value)} />

      <div class="night-actions">
        <button class="btn" onClick={() => relive()}><Icon name="relive" size={18} /> Relive this night</button>
        <button class="btn-ghost" onClick={() => void shareNight(s, n)}><Icon name="share" size={17} /> Share</button>
      </div>
      <p class="fine">Reliving shows the whole sky as it was, live: point your phone around or drag, and move through the night on the timeline.</p>
      <button class={`btn-ghost danger ${confirmDelete ? 'armed' : ''}`} onClick={del}>
        <Icon name="trash" size={16} /> {confirmDelete ? 'Tap again to delete this night' : 'Delete this night'}
      </button>
    </Sheet>
  );
}

// ---------- Reliving ----------
/** Across the top while reliving: which night, and the way back. */
export function ReliveBanner() {
  const r = reliving.value;
  const n = r && nights.value[r.night];
  if (!r || !n) return null;
  return (
    <button class="preview-banner relive-banner" onClick={() => endRelive()} style={{ top: 'calc(var(--safe-top) + 54px)' }}>
      <Icon name="relive" size={14} /> <span class="chip-text">{nightLabel(n.id, false)} · {n.place.name}</span> <span class="preview-back">Back to now</span>
    </button>
  );
}

/** At the bottom while reliving: the timeline of the night, with each thing seen on it. */
export function ReliveBar() {
  const r = reliving.value;
  const n = r && nights.value[r.night];
  void timeVersion.value;
  // Its height, for the chips that sit just above it (pointing, the paused chip).
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement.style;
    const ro = new ResizeObserver(() => root.setProperty('--relive-h', `${el.offsetHeight}px`));
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.removeProperty('--relive-h');
    };
  }, [!!n]);
  if (!r || !n) return null;
  const s = services.value!;
  const t = simNow().getTime();
  const span = r.to - r.from;
  const pos = (x: number) => `${((x - r.from) / span) * 100}%`;
  return (
    <div class="relive-bar" ref={ref} role="region" aria-label={`Reliving the night of ${nightLabel(n.id)}`}>
      <div class="relive-top">
        <span class="relive-time tnum">{fmtTime(t)}</span>
        <span class="relive-date">{fmtDate(t)}</span>
        <button class="icon-round" aria-label="Sky view: what the sky shows" onClick={() => (panel.value = 'layers')}><Icon name="layers" size={19} /></button>
        <button class="icon-round" aria-label="This night’s page" onClick={() => { openNight.value = n.id; panel.value = 'night'; }}><Icon name="nights" size={19} /></button>
      </div>
      <div class="relive-track">
        <input
          type="range" min={r.from} max={r.to} step={30e3} value={t} aria-label="Time on the night"
          aria-valuetext={fmtTime(t)} onInput={(e) => reliveAt(s, Number((e.target as HTMLInputElement).value))}
        />
        <div class="relive-marks" aria-hidden="true">
          {n.sightings.map((x) => <i key={x.id} style={{ left: pos(x.t) }} />)}
        </div>
      </div>
      <div class="relive-ends tnum"><span>{fmtTime(r.from)}</span><span>{fmtTime(r.to)}</span></div>
      {n.sightings.length > 0 && (
        <div class="relive-chips">
          {n.sightings.map((x) => {
            const on = Math.abs(x.t - t) < 60e3;
            return (
              <button key={x.id} class={`relive-chip ${on ? 'on' : ''}`} aria-current={on ? 'true' : undefined} onClick={() => reliveAt(s, x.t, x)}>
                <Icon name={iconFor(x)} size={14} /> {sightingName(s, x)} <span class="tnum">{fmtTime(x.t)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Sharing a night as a picture ----------
async function shareNight(s: AppServices, n: Night) {
  const W = 1080, H = 1350;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d')!;
  const font = '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif';
  g.fillStyle = '#03050a';
  g.fillRect(0, 0, W, H);
  const tz = n.place.timeZone;
  const [a, b] = nightSpan(n);
  g.textAlign = 'center';
  g.fillStyle = '#efcf8f';
  g.font = `600 28px ${font}`;
  g.fillText(`NIGHT OF ${nightLabel(n.id).toUpperCase()}`, W / 2, 96);
  g.fillStyle = '#edf0f7';
  g.font = `700 60px ${font}`;
  g.fillText(n.place.name, W / 2, 168);
  g.fillStyle = 'rgba(237, 240, 247, 0.62)';
  g.font = `400 30px ${font}`;
  g.fillText(a === b ? timeIn(a, tz) : `${timeIn(a, tz)} to ${timeIn(b, tz)}`, W / 2, 216);
  const disc = document.createElement('canvas');
  drawNightChart(disc, s, n, { size: 760, dpr: 1, labels: true, lines: true, magLimit: 5.2 });
  g.drawImage(disc, (W - 760) / 2, 262);
  // What was seen, in two columns.
  const items = n.sightings.slice(0, 6);
  g.textAlign = 'left';
  items.forEach((x, k) => {
    const col = k % 2, row = Math.floor(k / 2);
    const x0 = col ? W / 2 + 40 : 120, x1 = col ? W - 110 : W / 2 - 40, y0 = 1098 + row * 48;
    g.fillStyle = '#efcf8f';
    g.beginPath();
    g.arc(x0 - 22, y0 - 10, 6, 0, Math.PI * 2);
    g.fill();
    g.textAlign = 'right';
    g.fillStyle = 'rgba(237, 240, 247, 0.55)';
    g.font = `400 26px ${font}`;
    const time = timeIn(x.t, tz);
    g.fillText(time, x1, y0);
    const room = x1 - x0 - g.measureText(time).width - 16;
    g.textAlign = 'left';
    g.fillStyle = '#edf0f7';
    g.font = `600 30px ${font}`;
    let name = sightingName(s, x);
    while (name.length > 3 && g.measureText(name).width > room) name = `${name.slice(0, -2)}…`;
    g.fillText(name, x0, y0);
  });
  if (n.sightings.length > 6) {
    g.fillStyle = 'rgba(237, 240, 247, 0.55)';
    g.font = `400 26px ${font}`;
    g.textAlign = 'center';
    g.fillText(`and ${n.sightings.length - 6} more`, W / 2, 1098 + 3 * 48);
  }
  g.textAlign = 'center';
  g.fillStyle = 'rgba(239, 207, 143, 0.9)';
  g.font = `600 26px ${font}`;
  g.fillText('N O C T U R N E', W / 2, H - 52);
  const blob = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/jpeg', 0.92));
  if (!blob) return showToast('Could not make the picture');
  const name = `ahillah-night-${n.id}.jpg`;
  const file = new File([blob], name, { type: 'image/jpeg' });
  const text = `The night of ${nightLabel(n.id)} in ${n.place.name}: ${listNames(s, n, 4)}.`;
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  try {
    if (nav.share && nav.canShare?.({ files: [file] })) {
      await nav.share({ files: [file], text, title: 'A night with Ahillah' });
      return;
    }
  } catch (e) {
    if ((e as Error).name === 'AbortError') return;
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  showToast('Picture saved');
}

// ---------- Backup (from Settings) ----------
export async function backUpNights() {
  const count = Object.keys(nights.peek()).length;
  if (!count) return showToast('No nights to back up yet');
  const day = new Date().toISOString().slice(0, 10);
  const file = new File([backupText()], `ahillah-nights-${day}.json`, { type: 'application/json' });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  try {
    if (nav.share && nav.canShare?.({ files: [file] })) {
      await nav.share({ files: [file], title: 'Ahillah: your nights' });
      return;
    }
  } catch (e) {
    if ((e as Error).name === 'AbortError') return;
  }
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  showToast(`Backed up ${count} ${count === 1 ? 'night' : 'nights'}`);
}

export function restoreNights(file: File) {
  void file.text().then((text) => {
    const r = restoreFrom(text);
    if (!r) return showToast('That file is not an Ahillah backup of your nights');
    if (r.unsaved === 'full') return showToast('Your phone’s storage for Ahillah is full, so the backup could not be restored', 6000);
    if (r.unsaved) return showToast('Ahillah is not allowed to save on this phone, so the backup could not be restored. Check that your browser allows websites to save data.', 8000);
    if (!r.nightsAdded && !r.sightingsAdded) return showToast('Everything in that backup is already here');
    showToast(`Restored ${r.nightsAdded} ${r.nightsAdded === 1 ? 'night' : 'nights'}${r.sightingsAdded ? ` and ${r.sightingsAdded} ${r.sightingsAdded === 1 ? 'sighting' : 'sightings'}` : ''}`);
  });
}

